/**
 * ConnectorPickerDialog — Select a connector for import or export.
 *
 * Source mode: uses TableBrowser for rich table discovery with search,
 * pagination, column selection, and inline preview.
 * Destination mode: shows target table field + pre-selected output tables.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  CloudUpload as CloudIcon,
  CloudDownload as ImportIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import {
  listConnectors,
  discoverTables,
  previewTable,
} from '../../services/connectorApi';
import type { ConnectorConfig, DiscoveredTable } from '../../services/connectorApi';
import TableBrowser from '../common/TableBrowser';
import type { TableEntry } from '../common/TableBrowser';

/** Brand colors for common connector types. */
const CONNECTOR_COLORS: Record<string, string> = {
  postgres: '#336791',
  mysql: '#4479a1',
  mssql: '#cc2927',
  snowflake: '#29b5e8',
  bigquery: '#4285f4',
  redshift: '#8c4fff',
  s3: '#ff9900',
  gcs: '#4285f4',
  filesystem: '#64748b',
  filesystem_destination: '#64748b',
};

interface ConnectorPickerDialogProps {
  open: boolean;
  onClose: () => void;
  direction?: 'source' | 'destination';
  /**
   * Source mode: (connector, comma-joined table names, columnsByTable).
   * Destination mode: (connector, targetTable, undefined).
   */
  onSelect: (
    connector: ConnectorConfig,
    targetTable: string,
    columnsByTable?: Record<string, string[]>,
  ) => void;
  preSelectedTables?: string[];
  /** When true the dialog is exporting a pre-assembled byte output (XML/JSON).
   *  Format dropdown and per-table column filtering are hidden. */
  assemblyMode?: boolean;
  /** Fixed output format label shown in assembly mode ("xml", "json"). */
  assemblyFormat?: string;
}

const PAGE_SIZE = 50;

const ConnectorPickerDialog: React.FC<ConnectorPickerDialogProps> = ({
  open,
  onClose,
  direction = 'destination',
  onSelect,
  preSelectedTables,
  assemblyMode = false,
  assemblyFormat,
}) => {
  const isSource = direction === 'source';

  // Connector list
  const [connectors, setConnectors] = useState<ConnectorConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedConnector, setSelectedConnector] = useState<ConnectorConfig | null>(null);

  // Destination mode
  const [targetTable, setTargetTable] = useState('');
  const [destTargetNames, setDestTargetNames] = useState<Record<string, string>>({});
  const [destExpandedTables, setDestExpandedTables] = useState<Set<string>>(new Set());
  const [destSelectedColumns, setDestSelectedColumns] = useState<Map<string, string[]>>(new Map());

  // Source mode — TableBrowser state
  const [tables, setTables] = useState<TableEntry[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [selectedColumns, setSelectedColumns] = useState<Map<string, string[]>>(new Map());

  // Debounce search
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Fetch connector list
  const fetchConnectors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listConnectors({ direction });
      setConnectors(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  }, [direction]);

  // Reset on open
  useEffect(() => {
    if (open) {
      fetchConnectors();
      setSelectedConnector(null);
      setTargetTable('');
      setTables([]);
      setTotalCount(0);
      setSearch('');
      setPage(1);
      setSelectedTables(new Set());
      setSelectedColumns(new Map());
      setDestTargetNames({});
      setDestExpandedTables(new Set());
      setDestSelectedColumns(new Map());
    }
  }, [open, fetchConnectors]);

  // Fetch tables when connector selected (source mode)
  const fetchTables = useCallback(
    async (connectorId: string, searchStr: string, pageNum: number) => {
      setTablesLoading(true);
      try {
        const resp = await discoverTables({
          connectorId,
          search: searchStr || undefined,
          includeColumns: false,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
        const entries: TableEntry[] = (resp.tables || []).map(
          (t: DiscoveredTable): TableEntry => ({
            name: t.name,
            schemaName: t.schema_name || undefined,
            rowCountEstimate: t.row_count_estimate ?? undefined,
            columnCount: t.column_count ?? undefined,
          }),
        );
        setTables(entries);
        setTotalCount(resp.total_count);
      } catch {
        setTables([]);
        setTotalCount(0);
      } finally {
        setTablesLoading(false);
      }
    },
    [],
  );

  // When connector changes, fetch first page
  useEffect(() => {
    if (!isSource || !selectedConnector) {
      setTables([]);
      setTotalCount(0);
      setSelectedTables(new Set());
      setSelectedColumns(new Map());
      return;
    }
    setSearch('');
    setPage(1);
    fetchTables(selectedConnector.connector_id, '', 1);
  }, [isSource, selectedConnector, fetchTables]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        if (selectedConnector) {
          setPage(1);
          fetchTables(selectedConnector.connector_id, value, 1);
        }
      }, 300);
    },
    [selectedConnector, fetchTables],
  );

  // Page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      if (selectedConnector) {
        fetchTables(selectedConnector.connector_id, search, newPage);
      }
    },
    [selectedConnector, search, fetchTables],
  );

  // Expand table → fetch columns
  const handleExpandTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnector) return;
      // Mark loading
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, columnsLoading: true } : t)),
      );
      try {
        const resp = await discoverTables({
          connectorId: selectedConnector.connector_id,
          search: tableName,
          includeColumns: true,
          page: 1,
          pageSize: 1,
        });
        const match = resp.tables?.find((t: DiscoveredTable) => t.name === tableName);
        if (match?.columns) {
          setTables((prev) =>
            prev.map((t) =>
              t.name === tableName
                ? {
                    ...t,
                    columns: match.columns!.map((c) => ({
                      name: c.name,
                      dtype: c.dtype,
                      nullable: c.nullable,
                    })),
                    columnCount: match.columns!.length,
                    columnsLoading: false,
                  }
                : t,
            ),
          );
          // Initialize column selection with all columns
          setSelectedColumns((prev) => {
            const next = new Map(prev);
            if (!next.has(tableName)) {
              next.set(tableName, match.columns!.map((c) => c.name));
            }
            return next;
          });
        } else {
          setTables((prev) =>
            prev.map((t) =>
              t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t,
            ),
          );
        }
      } catch {
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t,
          ),
        );
      }
    },
    [selectedConnector],
  );

  // Preview table → fetch sample rows
  const handlePreviewTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnector) return;
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, previewLoading: true } : t)),
      );
      try {
        const tbl = tables.find((t) => t.name === tableName);
        const cols = selectedColumns.get(tableName);
        const resp = await previewTable({
          connectorId: selectedConnector.connector_id,
          tableName,
          schemaName: tbl?.schemaName,
          columns: cols && cols.length > 0 ? cols : undefined,
          limit: 25,
        });
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName
              ? { ...t, previewRows: resp.rows, previewLoading: false }
              : t,
          ),
        );
      } catch {
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName ? { ...t, previewRows: [], previewLoading: false } : t,
          ),
        );
      }
    },
    [selectedConnector, tables, selectedColumns],
  );

  // Column selection change
  const handleColumnSelectionChange = useCallback((table: string, cols: string[]) => {
    setSelectedColumns((prev) => {
      const next = new Map(prev);
      next.set(table, cols);
      return next;
    });
  }, []);

  const connectorColor = (type: string) => CONNECTOR_COLORS[type] || '#64748b';

  const connectorIcon = (type: string) => {
    if (['s3', 'gcs', 'filesystem', 'filesystem_destination'].includes(type)) {
      return <CloudIcon sx={{ fontSize: 18 }} />;
    }
    return <DatabaseIcon sx={{ fontSize: 18 }} />;
  };

  const handleConfirm = () => {
    if (!selectedConnector) return;
    if (assemblyMode) {
      // Assembly export — single file, no column filtering
      const ext = assemblyFormat || 'xml';
      const target = targetTable || `output.${ext}`;
      onSelect(selectedConnector, target);
      return;
    }
    if (isSource) {
      const tableNames = Array.from(selectedTables);
      // Build columnsByTable — only include tables where columns are actually filtered
      const columnsByTable: Record<string, string[]> = {};
      for (const tName of tableNames) {
        const cols = selectedColumns.get(tName);
        if (cols && cols.length > 0) {
          columnsByTable[tName] = cols;
        }
      }
      onSelect(selectedConnector, tableNames.join(','), columnsByTable);
    } else {
      // Destination mode — build column filters from destSelectedColumns
      const columnsByTable: Record<string, string[]> = {};
      Array.from(destSelectedColumns.entries()).forEach(([tName, cols]) => {
        if (cols.length > 0) {
          columnsByTable[tName] = cols;
        }
      });
      // Combine per-table target names into a single string if customized
      const customTargets = Object.entries(destTargetNames).filter(([, v]) => v.trim());
      const target = customTargets.length > 0
        ? customTargets.map(([src, dest]) => `${src}:${dest.trim()}`).join(',')
        : targetTable;
      onSelect(
        selectedConnector,
        target,
        Object.keys(columnsByTable).length > 0 ? columnsByTable : undefined,
      );
    }
  };

  const emptyLabel = isSource
    ? 'No source connectors configured'
    : 'No destination connectors configured';
  const titleLabel = isSource
    ? 'Import from Connector'
    : assemblyMode
      ? `Export as ${(assemblyFormat || 'XML').toUpperCase()}`
      : 'Select Export Destination';
  const TitleIcon = isSource ? ImportIcon : CloudIcon;
  const confirmLabel = isSource
    ? `Import ${selectedTables.size} table${selectedTables.size !== 1 ? 's' : ''}`
    : preSelectedTables && preSelectedTables.length > 0
      ? `Export ${preSelectedTables.length} table${preSelectedTables.length !== 1 ? 's' : ''}`
      : 'Configure Export';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={(isSource && selectedConnector) || (!isSource && selectedConnector && preSelectedTables && preSelectedTables.length > 1) ? 'md' : 'sm'}
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <TitleIcon color="primary" />
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          {titleLabel}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={isSource && selectedConnector ? { display: 'flex', flexDirection: 'column', minHeight: 420 } : undefined}>
        {/* Loading connectors */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && !error && connectors.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <DatabaseIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Set up a connector in Settings &rarr; Connectors
            </Typography>
          </Box>
        )}

        {/* ── Source mode: selected connector compact header ── */}
        {isSource && selectedConnector && !loading && (() => {
          const color = connectorColor(selectedConnector.connector_type);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                mb: 1.5,
                borderRadius: 1,
                bgcolor: alpha(color, 0.04),
                border: 1,
                borderColor: color,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.75,
                  bgcolor: alpha(color, 0.1),
                  color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {connectorIcon(selectedConnector.connector_type)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }} noWrap>
                  {selectedConnector.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {selectedConnector.connector_type}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setSelectedConnector(null)}
                sx={{ textTransform: 'none', fontSize: 11, px: 1, minWidth: 'auto' }}
              >
                Change
              </Button>
            </Box>
          );
        })()}

        {/* ── Source mode: TableBrowser (fills remaining space) ── */}
        {isSource && selectedConnector && !loading && (
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <TableBrowser
              tables={tables}
              loading={tablesLoading}
              totalCount={totalCount}
              searchValue={search}
              onSearchChange={handleSearchChange}
              selectionMode="multi"
              selectedTables={selectedTables}
              onSelectionChange={setSelectedTables}
              columnSelectionEnabled
              selectedColumns={selectedColumns}
              onColumnSelectionChange={handleColumnSelectionChange}
              onExpandTable={handleExpandTable}
              onPreviewTable={handlePreviewTable}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={handlePageChange}
            />
          </Box>
        )}

        {/* Connector list — only shown when no connector is selected in source mode,
            or always shown in destination mode */}
        {!loading && !(isSource && selectedConnector) &&
          connectors.map((conn) => {
            const isSelected =
              selectedConnector?.connector_id === conn.connector_id;
            const color = connectorColor(conn.connector_type);

            return (
              <Card
                key={conn.connector_id}
                variant="outlined"
                onClick={() => setSelectedConnector(conn)}
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  borderColor: isSelected ? color : 'divider',
                  bgcolor: isSelected ? alpha(color, 0.04) : 'transparent',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': {
                    borderColor: alpha(color, 0.5),
                    bgcolor: alpha(color, 0.02),
                  },
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: alpha(color, 0.1),
                        color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {connectorIcon(conn.connector_type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ fontSize: 13 }}
                        noWrap
                      >
                        {conn.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 10 }}
                      >
                        {conn.connector_type}
                      </Typography>
                    </Box>
                    <Chip
                      label={conn.status}
                      size="small"
                      color={conn.status === 'active' ? 'success' : 'default'}
                      variant="outlined"
                      sx={{
                        fontSize: 9,
                        height: 18,
                        textTransform: 'capitalize',
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

        {/* ── Destination mode: expandable table list + per-table target naming ── */}
        {!isSource && selectedConnector && !assemblyMode && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {preSelectedTables && preSelectedTables.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: 12, mb: 0.75, color: 'text.secondary' }}
                >
                  Exporting {preSelectedTables.length} table
                  {preSelectedTables.length !== 1 ? 's' : ''}
                </Typography>
                {preSelectedTables.map((t) => {
                  const isExpanded = destExpandedTables.has(t);
                  const destCols = destSelectedColumns.get(t);
                  return (
                    <Box key={t} sx={{ mb: 0.75 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.75,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { borderColor: alpha('#7c3aed', 0.3), bgcolor: alpha('#7c3aed', 0.02) },
                        }}
                        onClick={() =>
                          setDestExpandedTables((prev) => {
                            const next = new Set(prev);
                            if (next.has(t)) next.delete(t);
                            else next.add(t);
                            return next;
                          })
                        }
                      >
                        <Chip
                          label={t}
                          size="small"
                          sx={{
                            fontSize: 11,
                            height: 22,
                            fontFamily: '"Fira Code", monospace',
                            bgcolor: alpha('#2563eb', 0.08),
                            color: '#2563eb',
                          }}
                        />
                        <Box sx={{ flex: 1 }} />
                        {destCols && (
                          <Chip
                            label={`${destCols.length} cols`}
                            size="small"
                            sx={{ fontSize: 9, height: 16, bgcolor: alpha('#059669', 0.1), color: '#059669' }}
                          />
                        )}
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Box>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ ml: 2, mr: 1, mt: 0.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafbfc' }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Target name (optional)"
                            placeholder={t}
                            value={destTargetNames[t] || ''}
                            onChange={(e) =>
                              setDestTargetNames((prev) => ({ ...prev, [t]: e.target.value }))
                            }
                            sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: 12 } }}
                          />
                          {/* Column deselection will work when workspace tables have column metadata */}
                          {destCols && destCols.length > 0 && (
                            <Box sx={{ maxHeight: 140, overflow: 'auto' }}>
                              {destCols.map((col) => (
                                <Stack
                                  key={col}
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ py: 0.2, px: 0.5, borderRadius: 0.5, '&:hover': { bgcolor: alpha('#2563eb', 0.04) } }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked
                                    onChange={() => {
                                      setDestSelectedColumns((prev) => {
                                        const next = new Map(prev);
                                        const cur = next.get(t) ?? [];
                                        next.set(t, cur.filter((c) => c !== col));
                                        return next;
                                      });
                                    }}
                                    sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                                  />
                                  <Typography sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace' }}>
                                    {col}
                                  </Typography>
                                </Stack>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}
            {/* Global target name fallback — only when no per-table naming is used */}
            {(!preSelectedTables || preSelectedTables.length <= 1) && (
              <TextField
                fullWidth
                size="small"
                label="Target Table / Path (optional)"
                placeholder="e.g. public.cleaned_data"
                value={targetTable}
                onChange={(e) => setTargetTable(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={
            !selectedConnector || (isSource && selectedTables.size === 0)
          }
          sx={{ textTransform: 'none' }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectorPickerDialog;
